import { type FC, Fragment, useCallback, useEffect, useState } from 'react';

import { AccommodationPlaceholder } from '@vakantiesnl/components/src/atoms/AccommodationPlaceholder';
import { Alert } from '@vakantiesnl/components/src/atoms/Alert';
import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { StepTitle } from '@vakantiesnl/components/src/blocks/accommodation/StepTitle/StepTitle';
import { FlightSelector } from '@vakantiesnl/components/src/molecules/FlightSelector';
import { MobileFilterMenuWrapper } from '@vakantiesnl/components/src/molecules/MobileFilterMenuWrapper';
import { FlightFilterMenu } from '@vakantiesnl/components/src/organisms/FlightFilterMenu';
import { numberOfFlightFilters } from '@vakantiesnl/components/src/utils/numberOfFlightFilters';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { useAccommodationContext } from '@vakantiesnl/services/src/context/useAccommodationContext';
import { useTripFilterContext } from '@vakantiesnl/services/src/context/useTripFilterContext';
import { useFilteredFlights } from '@vakantiesnl/services/src/hooks/accommodation/flightFilters';
import { useGetAccommodationByEntityId } from '@vakantiesnl/services/src/hooks/queries';
import { useAccoOfferStore } from '@vakantiesnl/services/src/stores/accommodationStore';
import { getMonthName } from '@vakantiesnl/services/src/util/dateUtils';
import { inject } from '@vakantiesnl/services/src/util/template';
import { type Booking, type Search } from '@vakantiesnl/types';
import { ActionLabels } from '@vakantiesnl/types/src/booking';
import { type OfferItem } from '@vakantiesnl/types/src/search';
import dynamic from 'next/dynamic';

const CompassLoader = dynamic(
	() =>
		import('@vakantiesnl/components/src/atoms/CompassLoader').then((mod) => ({
			default: mod.CompassLoader,
		})),
	{ ssr: false },
);

import { useCustomStyles } from './Flights.styles';

const DEFAULT_AMOUNT_OF_FLIGHTS = 4;
const FLIGHTS_PAGE_SIZE = 5;

const Flights: FC = () => {
	const { classes } = useCustomStyles();
	const microCopies = useMicroCopyContext();

	const {
		state: filterState,
		dispatch: { updateSelectedFlight, updateSelectedRoomType, updateSelectedMealPlan, updateSelectedDirectOnly },
	} = useTripFilterContext();
	const { accommodation } = useGetAccommodationByEntityId();
	const {
		state: { flightData: flightAlternatives },
		dispatch: { setFlightOffer },
	} = useAccommodationContext();

	const isLoadingDepartureDateOffers = useAccoOfferStore((s) => s.isDepartureDateLoading);
	const selectedDepartureDateOffer = useAccoOfferStore((s) => s.departureDateOffer);

	const [flightFilters, setFlightFilters] = useState<Booking.FlightFilters>({
		directOnly: filterState.selected.directOnly,
	});
	const [nrOfFlightFilters, setNrOfFlightFilters] = useState(numberOfFlightFilters(flightFilters));
	const { flights } = useFilteredFlights(flightAlternatives, flightFilters);
	const [actionLabel, setActionLabel] = useState('');

	const [itemsToShow, setItemsToShow] = useState(DEFAULT_AMOUNT_OF_FLIGHTS);

	// Reset the items to show when selecting a different offer
	useEffect(() => {
		setItemsToShow(DEFAULT_AMOUNT_OF_FLIGHTS);
	}, [selectedDepartureDateOffer?.id]);

	// Expand the list by default when an offer further down in the list was selected
	useEffect(() => {
		if (flightAlternatives?.loading) return;

		const offerIndex = flights.findIndex((f) => f.flightId === flightAlternatives?.selectedFlightOffer?.flightId);

		setItemsToShow((prev) => Math.max(offerIndex + 1, prev));
	}, [flights, flightAlternatives]);

	const updateFilters = useCallback((flightFilters: Booking.FlightFilters) => {
		setNrOfFlightFilters(numberOfFlightFilters(flightFilters));
		setFlightFilters(flightFilters);
	}, []);

	const cheapestDatePrice = flights.length > 0 ? flights[0].pricePerAdult : 0;
	const getPriceDiff = useCallback(
		({ pricePerAdult }: Search.OfferItem) =>
			cheapestDatePrice - pricePerAdult === 0 ? undefined : pricePerAdult - cheapestDatePrice,
		[cheapestDatePrice],
	);

	const onFlightSelect = useCallback(
		(offer: OfferItem, key: number) => (): void => {
			if (flightAlternatives && !flightAlternatives?.loading) {
				updateSelectedRoomType(undefined);
				updateSelectedMealPlan(undefined);
				setFlightOffer(offer);

				const outboundCode = offer.outbound.meta?.flightNumbers.join('');
				const inboundCode = offer.inbound.meta?.flightNumbers.join('');

				if (outboundCode && inboundCode) {
					updateSelectedFlight({ outbound: outboundCode, inbound: inboundCode });
				}

				import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
					import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ flightSelect }) => {
						track(flightSelect(accommodation, offer, key));
					}),
				);
			}
		},
		[
			flightAlternatives,
			setFlightOffer,
			updateSelectedRoomType,
			updateSelectedMealPlan,
			updateSelectedFlight,
			accommodation,
		],
	);

	useEffect(() => {
		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ flightPagination }) => {
				track(
					flightPagination({
						resultsShown: itemsToShow,
						resultsTotal: flights.length,
						heliosId: accommodation.heliosId,
						actionLabel: actionLabel,
					}),
				);
			}),
		);
	}, [actionLabel, accommodation.giataId, accommodation.heliosId, flights.length, itemsToShow]);

	const handleShowMoreFlights = useCallback(() => {
		setActionLabel(ActionLabels.showMoreFlights);
		/**
		 * @if the items to show + page size is bigger then the amount of total flights; set the flights size as page size
		 * @else increase the items to show with the page size
		 */
		if (itemsToShow + FLIGHTS_PAGE_SIZE > flights.length) setItemsToShow(flights.length);
		else setItemsToShow((itemsToShow) => itemsToShow + FLIGHTS_PAGE_SIZE);
	}, [flights.length, itemsToShow]);

	if (!flightAlternatives || isLoadingDepartureDateOffers || !selectedDepartureDateOffer) {
		return (
			<div data-cy="flights-selector">
				<StepTitle title={microCopies['accommodation.flight.title']} className={classes.flightsHeader} />
				{flightAlternatives === null ? (
					<Alert variant="warning" text={microCopies['flightfilters.noresults']} />
				) : (
					<AccommodationPlaceholder title={microCopies['accommodation.flight.placeholder_text']} />
				)}
			</div>
		);
	}

	return (
		<div data-cy="flights-selector">
			<StepTitle title={microCopies['accommodation.flight.title']} className={classes.flightsHeader} />

			<div className={classes.flightContainer}>
				<MobileFilterMenuWrapper
					microCopies={microCopies}
					drawerTitle={microCopies['filters.flights.drawerTitle']}
					openButtonText={microCopies['filters.chooseSelection']}
					numberOfActiveFilters={nrOfFlightFilters}
				>
					<FlightFilterMenu
						setFilters={updateFilters}
						flightFilters={flightFilters}
						accommodation={accommodation}
						nrOfFlightFilters={nrOfFlightFilters}
						updateSelectedDirectOnly={updateSelectedDirectOnly}
						setActionLabel={setActionLabel}
						actionLabel={actionLabel}
					/>
				</MobileFilterMenuWrapper>
				<div>
					{flights.length === 0 && !flightAlternatives.loading ? (
						<Alert variant="warning" text={microCopies['flightfilters.noresults']} />
					) : (
						<Typography variant="headingSm" as="h4" className={classes.recommendedFlightTitle}>
							{inject(microCopies['accommodation.flight.recommended_suggestion'], {
								from: `${new Date(selectedDepartureDateOffer.outbound.departureDateTime).getDate()} ${getMonthName(
									new Date(selectedDepartureDateOffer.outbound.departureDateTime),
								)}`,
								to: `${new Date(selectedDepartureDateOffer.inbound.departureDateTime).getDate()} ${getMonthName(
									new Date(selectedDepartureDateOffer.inbound.departureDateTime),
								)}`,
							})}
						</Typography>
					)}
					{flightAlternatives.loading && <CompassLoader />}
					<div className={classes.flights}>
						{flights.slice(0, itemsToShow).map((flightOffer, key) => {
							// eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
							const tags = {
								isCheapest: key === 0,
								recommended: selectedDepartureDateOffer?.flightId === flightOffer.flightId,
							};

							return (
								<Fragment key={flightOffer.flightId}>
									{key === 1 && (
										<>
											<Typography variant="headingSm" as="h4">
												{microCopies['common.alternative_flights']}
											</Typography>
											<Typography variant="bodyMd" as="p">
												{microCopies['accommodation.alternativeFlight.description']}
											</Typography>
										</>
									)}
									<FlightSelector
										tags={tags}
										inbound={flightOffer.inbound}
										outbound={flightOffer.outbound}
										isLuggageInclusive={flightOffer.hasLuggageIncluded}
										isTransferInclusive={flightOffer.hasTransferIncluded}
										microCopy={microCopies}
										selected={
											flightAlternatives.loading === false &&
											flightAlternatives.selectedFlightOffer?.flightId === flightOffer.flightId
										}
										handleSelected={onFlightSelect(flightOffer, key)}
										id={flightOffer.flightId}
										extraPrice={getPriceDiff(flightOffer)}
									/>
								</Fragment>
							);
						})}
						{flights.length > itemsToShow && (
							<div>
								<Button variant="secondary" onClick={handleShowMoreFlights} data-cy="flight-pagination">
									{microCopies['filters.showMoreFlights']}
								</Button>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Flights;
